import { useMap } from "@vis.gl/react-maplibre";
import { useEffect, type RefObject } from "react";

import { Log } from "@ryde/shared";

import { getAliveMap } from "./maplibre-map.utils";
import {
  AUTO_PAN_DURATION_MS,
  AUTO_PAN_PADDING_PX,
} from "./use-maplibre-popup-auto-pan.constants";
import { resolveAxisPan } from "./use-maplibre-popup-auto-pan.utils";

/**
 * Slides the map so an open popup is fully visible — Leaflet's `autoPan`, which MapLibre has no equivalent
 * for. Without it a popup opened near an edge is simply clipped, and on a tip-anchored pin near the top of
 * the viewport most of the card can be off-screen.
 *
 * Measured from the popup's real rendered box rather than from the marker, because the popup's height
 * depends on its content — a station with six passengers needs far more room than one with none.
 *
 * @param contentRef an element inside the popup; the popup box itself is found from it.
 * @param contentKey changes when the content changes size after opening (a lookup's answer arriving), so the
 * now taller popup is measured again — the bottom-anchored box grows upward, past the top edge.
 */
export const useMapLibrePopupAutoPan = ({
  isOpen,
  contentRef,
  contentKey,
}: {
  isOpen: boolean;
  contentRef: RefObject<HTMLElement | null>;
  contentKey?: string;
}): void => {
  const { current: mapRef } = useMap();

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!isOpen) {
      return;
    }

    /*
     * `useMap` resolves through `@vis.gl/react-maplibre`'s context, so this hook only works from a component
     * INSIDE the map. Called from the component that renders the map it finds nothing and does nothing —
     * silently, which is how the landing map shipped without auto-pan. Say so instead.
     */
    if (!map) {
      if (!mapRef) {
        Log.warn(
          "useMapLibrePopupAutoPan found no map: it must be called from inside the map subtree",
        );
      }

      return;
    }

    // A frame late on purpose: the popup has to be laid out before its box can be measured.
    const frameId = requestAnimationFrame(() => {
      const aliveMap = getAliveMap(mapRef);
      if (!aliveMap) {
        return;
      }

      const popup = contentRef.current?.closest(".maplibregl-popup");
      const container = aliveMap.getContainer();

      if (!popup || !container) {
        return;
      }

      const popupRect = popup.getBoundingClientRect();
      const mapRect = container.getBoundingClientRect();

      const overflowLeft = mapRect.left + AUTO_PAN_PADDING_PX - popupRect.left;
      const overflowRight =
        popupRect.right - (mapRect.right - AUTO_PAN_PADDING_PX);
      const overflowTop = mapRect.top + AUTO_PAN_PADDING_PX - popupRect.top;
      const overflowBottom =
        popupRect.bottom - (mapRect.bottom - AUTO_PAN_PADDING_PX);

      const panX = resolveAxisPan(overflowLeft, overflowRight);
      const panY = resolveAxisPan(overflowTop, overflowBottom);

      if (!panX && !panY) {
        return;
      }

      try {
        aliveMap.panBy([panX, panY], { duration: AUTO_PAN_DURATION_MS });
      } catch (error) {
        Log.warn("MapLibre popup auto-pan exception", error);
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [isOpen, mapRef, contentRef, contentKey]);
};
